import React, { useEffect } from 'react';
import {
  BooleanInput,
  NumberInput,
  maxValue,
  minValue,
  number,
  required,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from '@mui/material';
import { EventFeesTab } from './EventFeesTab';

/** Checkbox işaretli = kanalda göster; alanlar "hideFrom*" olarak saklanır. */
const VISIBILITY_CHANNELS = [
  { source: 'hideFromWeb', labelKey: 'form.channel_web', fallback: 'Web' },
  { source: 'hideFromMobile', labelKey: 'form.channel_mobile', fallback: 'Mobil' },
  { source: 'hideFromPartners', labelKey: 'form.channel_sales_point', fallback: 'SalesPoint' },
];

const ChannelVisibilityCheckboxes = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const values = useWatch({ name: VISIBILITY_CHANNELS.map((c) => c.source) });

  return (
    <Box>
      <FormGroup row sx={{ gap: 2 }}>
        {VISIBILITY_CHANNELS.map((channel, i) => (
          <FormControlLabel
            key={channel.source}
            control={
              <Checkbox
                size="small"
                checked={values?.[i] !== true}
                onChange={(e) => setValue(channel.source, !e.target.checked, { shouldDirty: true })}
              />
            }
            label={translate(channel.labelKey, channel.fallback)}
          />
        ))}
      </FormGroup>
      <Typography variant="caption" sx={{ color: '#6b7280' }}>
        {translate(
          'form.channel_visibility_hint',
          'İşaretli kanallarda etkinlik gösterilir (liste, arama, etkinlik sayfası). SalesPoint: satış ortaklarına giden JSON listesi.',
        )}
      </Typography>
    </Box>
  );
};

const NESTED_SX = { pl: 4, mt: -1, mb: 1.5, borderLeft: '2px solid #e5e7eb', ml: 1.5 };

const HOME_HIDE_FIELDS = [
  { source: 'hideFromWebHome', labelKey: 'form.channel_web', fallback: 'Web' },
  { source: 'hideFromMobileHome', labelKey: 'form.channel_mobile', fallback: 'Mobil' },
];

/** Açılınca Web / Mobil checkbox'ları; işaretli olan platformun ana sayfasında gizlenir. */
const HomeHideControl = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const values = useWatch({ name: HOME_HIDE_FIELDS.map((f) => f.source) });
  const anyHidden = (values || []).some((v) => v === true);
  const [open, setOpen] = React.useState(anyHidden);

  React.useEffect(() => {
    if (anyHidden) setOpen(true);
  }, [anyHidden]);

  const setAll = (hidden) => {
    HOME_HIDE_FIELDS.forEach((f) => setValue(f.source, hidden, { shouldDirty: true }));
  };

  return (
    <Box>
      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Switch
            checked={open}
            onChange={(e) => {
              const next = e.target.checked;
              setOpen(next);
              setAll(next);
            }}
          />
        }
        label={translate('form.hide_from_home', 'Ana sayfada gizle')}
      />
      <Typography
        variant="caption"
        component="p"
        sx={{ color: '#6b7280', mt: -0.5, mb: 1, ml: 1.75 }}
      >
        {translate(
          'form.hide_from_home_hint',
          'Açıksa bu etkinlik seçili ana sayfalardaki bölümlerde gösterilmez.',
        )}
      </Typography>
      <Collapse in={open} timeout={150} unmountOnExit>
        <Box sx={NESTED_SX}>
          <FormGroup row sx={{ gap: 2 }}>
            {HOME_HIDE_FIELDS.map((field, i) => (
              <FormControlLabel
                key={field.source}
                control={
                  <Checkbox
                    size="small"
                    checked={values?.[i] === true}
                    onChange={(e) =>
                      setValue(field.source, e.target.checked, { shouldDirty: true })
                    }
                  />
                }
                label={translate(field.labelKey, field.fallback)}
              />
            ))}
          </FormGroup>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            {translate(
              'form.hide_from_home_channels_hint',
              'İşaretli platformların ana sayfasında etkinlik gösterilmez.',
            )}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

/** İşaretliyse satış etkinlik bitiş tarihine kadar açık; değilse başlangıçta kapanır. */
const SellUntilEndCheckbox = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const sellUntilEnd = useWatch({ name: 'sellUntilEnd' });

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={sellUntilEnd === true}
            onChange={(e) => setValue('sellUntilEnd', e.target.checked, { shouldDirty: true })}
          />
        }
        label={translate('form.sell_until_end', 'Etkinlik bitene kadar satışta kalsın')}
      />
      <Typography variant="caption" component="p" sx={{ color: '#6b7280', mt: -0.5 }}>
        {translate(
          'form.sell_until_end_hint',
          'İşaretliyse satış etkinliğin bitiş tarihine kadar açık kalır. İşaretli değilse etkinlik (veya seans) başladığında satış kapanır.',
        )}
      </Typography>
    </Box>
  );
};

const URGENCY_SWITCH_SX = { my: 0, '& .MuiFormControlLabel-root': { my: 0 } };
const URGENCY_ROW_SX = {
  display: 'flex',
  flexWrap: { xs: 'wrap', sm: 'nowrap' },
  alignItems: 'flex-start',
  gap: 2,
  minHeight: 44,
};
const URGENCY_SWITCH_BOX_SX = { flexShrink: 0, width: { xs: '100%', sm: 300 }, pt: '6px' };
const URGENCY_INPUT_SX = { flex: 1, minWidth: 0, mt: 0, mb: 0.5 };

const URGENCY_MAX = {
  hours: 128,
  days: 90,
  tickets: 1000,
};

/**
 * Event Settings Tab — status, homepage visibility, urgency badges, fees & commissions.
 * Refund settings live on Products / Stocks tab.
 */
export const EventSettingsTab = ({
  permissions = {},
  record = {},
  systemParams,
  isEdit = false,
}) => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const isAdmin = permissions?.crole === 'admin';
  const daysEnabled = useWatch({ name: 'urgency.daysEnabled' });
  const hoursEnabled = useWatch({ name: 'urgency.hoursEnabled' });
  const ticketsEnabled = useWatch({ name: 'urgency.ticketsEnabled' });
  const isActive = useWatch({ name: 'isActive' });
  const isVerified = useWatch({ name: 'isVerified' });

  useEffect(() => {
    if (record && Object.keys(record).length === 0) {
      setValue('isActive', isAdmin);
      setValue('isVerified', isAdmin);
      setValue('refundEnabled', true);
      setValue('refundDeadlineAt', null);
    }
  }, [isAdmin, record, setValue]);

  return (
    <React.Fragment>
      <h3 style={{ marginTop: 0 }}>{translate('form.visibility_settings', 'Görünüm Ayarları')}</h3>

      {isAdmin ? (
        <>
          <BooleanInput
            source="isActive"
            label={translate('form.show_event_on_website', 'Show this event on the website?')}
            helperText={translate(
              'form.is_active_hint',
              "Please note that the event should be 'saved' for this change to take effect.",
            )}
          />
          <Collapse in={Boolean(isActive)} timeout={150} unmountOnExit>
            <Box sx={NESTED_SX}>
              <ChannelVisibilityCheckboxes />
            </Box>
          </Collapse>
          <HomeHideControl />
          <BooleanInput
            source="isVerified"
            label={translate('form.verified_event', 'Etkinliği Satışa Aç')}
            helperText={translate('form.is_verified_hint', 'Is event verified by the admin?')}
          />
          <Collapse in={Boolean(isVerified)} timeout={150} unmountOnExit>
            <Box sx={NESTED_SX}>
              <SellUntilEndCheckbox />
            </Box>
          </Collapse>
        </>
      ) : (
        <>
          <Box sx={NESTED_SX}>
            <ChannelVisibilityCheckboxes />
          </Box>
          <HomeHideControl />
        </>
      )}

      <h3 style={{ marginTop: 24 }}>
        {translate('form.auto_urgency_badges', 'Otomatik Etiketler')}
      </h3>
      <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 12 }}>
        {translate(
          'form.urgency_hint',
          'Kartta tek kırmızı etiket çıkar. Koşullar tutarsa öncelik: saat → bilet → gün. Örnek: son 20 saatteyse saat; değil ama az bilet kaldıysa bilet; değil ama X gün kaldıysa gün. Etiket kalan gerçek değeri yazar (ör. Son 7 bilet).',
        )}
      </p>

      <Box sx={URGENCY_ROW_SX}>
        <Box sx={URGENCY_SWITCH_BOX_SX}>
          <BooleanInput
            source="urgency.hoursEnabled"
            label={translate('form.urgency_hours_label', 'Son X saat kala etiket göster')}
            defaultValue={false}
            helperText={false}
            sx={URGENCY_SWITCH_SX}
          />
        </Box>
        {hoursEnabled ? (
          <NumberInput
            size="small"
            sx={URGENCY_INPUT_SX}
            source="urgency.hoursThreshold"
            label={translate('form.urgency_hours_threshold', 'X saat (eşik)')}
            min={1}
            max={URGENCY_MAX.hours}
            defaultValue={24}
            parse={(v) => (v === '' || v == null ? null : Number(v))}
            validate={[
              required(translate('form.hours_threshold_required', 'Saat eşiği zorunludur.')),
              number(translate('form.valid_number_required', 'Geçerli bir sayı girin.')),
              minValue(1, translate('form.hours_threshold_min', 'Saat eşiği en az 1 olmalıdır.')),
              maxValue(
                URGENCY_MAX.hours,
                translate(
                  'form.hours_threshold_max',
                  `Saat eşiği en fazla ${URGENCY_MAX.hours} olabilir.`,
                  {
                    max: URGENCY_MAX.hours,
                  },
                ),
              ),
            ]}
            fullWidth
            helperText={translate(
              'form.hours_threshold_hint',
              `Etkinliğe bu kadar saat veya daha az kaldığında 'Son N saat' (1–${URGENCY_MAX.hours}).`,
              { max: URGENCY_MAX.hours },
            )}
          />
        ) : null}
      </Box>

      <Box sx={URGENCY_ROW_SX}>
        <Box sx={URGENCY_SWITCH_BOX_SX}>
          <BooleanInput
            source="urgency.ticketsEnabled"
            label={translate('form.urgency_tickets_label', 'Son X bilet kala etiket göster')}
            defaultValue={false}
            helperText={false}
            sx={URGENCY_SWITCH_SX}
          />
        </Box>
        {ticketsEnabled ? (
          <NumberInput
            size="small"
            sx={URGENCY_INPUT_SX}
            source="urgency.ticketsThreshold"
            label={translate('form.urgency_tickets_threshold', 'X bilet (eşik)')}
            min={1}
            max={URGENCY_MAX.tickets}
            defaultValue={10}
            parse={(v) => (v === '' || v == null ? null : Number(v))}
            validate={[
              required(translate('form.tickets_threshold_required', 'Bilet eşiği zorunludur.')),
              number(translate('form.valid_number_required', 'Geçerli bir sayı girin.')),
              minValue(
                1,
                translate('form.tickets_threshold_min', 'Bilet eşiği en az 1 olmalıdır.'),
              ),
              maxValue(
                URGENCY_MAX.tickets,
                translate(
                  'form.tickets_threshold_max',
                  `Bilet eşiği en fazla ${URGENCY_MAX.tickets} olabilir.`,
                  { max: URGENCY_MAX.tickets },
                ),
              ),
            ]}
            fullWidth
            helperText={translate(
              'form.tickets_threshold_hint',
              `Kalan bilet (koltuklu dahil) bu sayıya eşit/küçükse 'Son N bilet' (1–${URGENCY_MAX.tickets}). Saat penceresi dışındayken geçerlidir.`,
              { max: URGENCY_MAX.tickets },
            )}
          />
        ) : null}
      </Box>

      <Box sx={URGENCY_ROW_SX}>
        <Box sx={URGENCY_SWITCH_BOX_SX}>
          <BooleanInput
            source="urgency.daysEnabled"
            label={translate('form.urgency_days_label', 'Son X gün kala etiket göster')}
            defaultValue={false}
            helperText={false}
            sx={URGENCY_SWITCH_SX}
          />
        </Box>
        {daysEnabled ? (
          <NumberInput
            size="small"
            sx={URGENCY_INPUT_SX}
            source="urgency.daysThreshold"
            label={translate('form.urgency_days_threshold', 'X gün (eşik)')}
            min={1}
            max={URGENCY_MAX.days}
            defaultValue={3}
            parse={(v) => (v === '' || v == null ? null : Number(v))}
            validate={[
              required(translate('form.days_threshold_required', 'Gün eşiği zorunludur.')),
              number(translate('form.valid_number_required', 'Geçerli bir sayı girin.')),
              minValue(1, translate('form.days_threshold_min', 'Gün eşiği en az 1 olmalıdır.')),
              maxValue(
                URGENCY_MAX.days,
                translate(
                  'form.days_threshold_max',
                  `Gün eşiği en fazla ${URGENCY_MAX.days} olabilir.`,
                  {
                    max: URGENCY_MAX.days,
                  },
                ),
              ),
            ]}
            fullWidth
            helperText={translate(
              'form.days_threshold_hint',
              `Kalan gün bu değere eşit/küçükse 'Son N gün' (1–${URGENCY_MAX.days}). Saat veya bilet etiketi yoksa gösterilir.`,
              { max: URGENCY_MAX.days },
            )}
          />
        ) : null}
      </Box>

      <h3 style={{ marginTop: 24 }}>{translate('form.fees', 'Ücretler ve Komisyonlar')}</h3>
      <EventFeesTab systemParams={systemParams} permissions={permissions} isEdit={isEdit} />
    </React.Fragment>
  );
};
